// Cookie consent state — AEPD-compliant
// Categories: analytics + advertising (necessary is always granted)
// Persisted in localStorage for 12 months
// Never emits real Meta events (pixel or CAPI) unless `advertising` is granted.

const STORAGE_KEY = "sircs_cookie_consent_v1";
const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * @typedef {Object} ConsentState
 * @property {boolean} analytics
 * @property {boolean} advertising
 * @property {number} ts        - timestamp when the choice was made
 */

/** Read stored consent. Returns null if unset or expired. */
export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const ts = Number(parsed.ts) || 0;
    if (!ts || Date.now() - ts > TWELVE_MONTHS_MS) return null;
    return {
      analytics: !!parsed.analytics,
      advertising: !!parsed.advertising,
      ts,
    };
  } catch (_) {
    return null;
  }
}

/** True when the user has made an explicit decision that is still valid. */
export function hasDecided() {
  return getConsent() !== null;
}

/** True when the user has actively granted the advertising category. */
export function hasAdConsent() {
  const c = getConsent();
  return !!(c && c.advertising);
}

/** True when the user has actively granted the analytics category. */
export function hasAnalyticsConsent() {
  const c = getConsent();
  return !!(c && c.analytics);
}

/** Persist a decision and notify subscribers. */
export function saveConsent({ analytics, advertising }) {
  const state = {
    analytics: !!analytics,
    advertising: !!advertising,
    ts: Date.now(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_) {
    /* storage disabled — treat as ephemeral */
  }
  try {
    window.dispatchEvent(new CustomEvent("sircs-consent-changed", { detail: state }));
  } catch (_) {
    /* ignore */
  }
  return state;
}

/** Ask the banner to reopen the settings panel. */
export function openSettings() {
  try {
    window.dispatchEvent(new Event("sircs-cookie-settings-open"));
  } catch (_) {
    /* ignore */
  }
}

/** Subscribe to consent changes. Returns an unsubscribe fn. */
export function onConsentChange(cb) {
  const handler = (e) => cb(e.detail);
  window.addEventListener("sircs-consent-changed", handler);
  return () => window.removeEventListener("sircs-consent-changed", handler);
}
